import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'glass' | 'outline' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
    className?: string;
}

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    children,
    className = '',
    ...props
}) => {
    const baseStyles = [
        'inline-flex items-center justify-center',
        'font-bold uppercase tracking-wider',
        'rounded-xl',
        'transition-all duration-300',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'active:scale-95',
    ].join(' ');

    const variants: Record<string, string> = {
        primary: [
            'bg-primary text-white',
            'hover:bg-primary-dark',
            'shadow-md hover:shadow-lg',
            // Dark mode
            'dark:bg-primary-light dark:hover:bg-primary',
            'dark:shadow-premium-dark dark:hover:shadow-premium-dark-hover',
        ].join(' '),

        secondary: [
            'bg-secondary text-primary',
            'hover:bg-secondary-dark',
            'shadow-md hover:shadow-lg',
            // Dark mode
            'dark:bg-secondary dark:text-surface-dark dark:hover:bg-secondary-dark',
        ].join(' '),

        glass: [
            'bg-white/10 backdrop-blur-md',
            'border border-white/20',
            'text-white',
            'hover:bg-white/20',
            // Dark mode
            'dark:bg-white/5 dark:border-white/10 dark:hover:bg-white/15',
        ].join(' '),

        outline: [
            'bg-transparent',
            'border-2 border-primary text-primary',
            'hover:bg-primary hover:text-white',
            // Dark mode
            'dark:border-on-surface-dark dark:text-on-surface-dark',
            'dark:hover:bg-primary-light dark:hover:text-white dark:hover:border-primary-light',
        ].join(' '),

        ghost: [
            'bg-transparent text-primary',
            'hover:bg-primary/5',
            // Dark mode
            'dark:text-on-surface-dark dark:hover:bg-white/5',
        ].join(' '),
    };

    const sizes: Record<string, string> = {
        sm: 'px-4 py-2 text-[0.625rem]',   // ~10px em rem context
        md: 'px-6 py-3 text-xs',           // 12px
        lg: 'px-8 py-4 text-sm',           // 14px
    };

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
};
